import { useState, useRef, useCallback } from 'react';
import { Upload, X, Loader2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import StrategyImageCropper from './StrategyImageCropper';
import { strategyUploadApi } from '@/services/strategyUploadApi';
import { usePredefinedImages } from '@/hooks/useStrategies';
import {
  getFileTypeError,
  getFileExtension,
  getCroppedStrategyImage,
  compressStrategyImageIfNeeded,
  uploadToS3WithRetry,
  STRATEGY_IMAGE_DIMENSIONS,
} from '@/utils/strategyImageUtils';
import type { UploadedStrategyImage, PredefinedImage } from '@/types/strategy';

interface StrategyImageUploadProps {
  value: UploadedStrategyImage | null;
  onChange: (image: UploadedStrategyImage | null) => void;
  required?: boolean;
}

export default function StrategyImageUpload({
  value,
  onChange,
  required = false,
}: StrategyImageUploadProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showPredefined, setShowPredefined] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: predefinedImages } = usePredefinedImages();

  // --- Custom Upload Flow ---
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const error = getFileTypeError(file);
    if (error) {
      toast.error(error);
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result as string);
    reader.readAsDataURL(file);
  }, []);

  const handleCropComplete = useCallback(
    async (croppedAreaPixels: { x: number; y: number; width: number; height: number }) => {
      if (!imageSrc || !selectedFile) return;

      try {
        setIsUploading(true);

        const croppedBlob = await getCroppedStrategyImage(imageSrc, croppedAreaPixels);
        const finalBlob = await compressStrategyImageIfNeeded(croppedBlob);
        const fileType = getFileExtension(selectedFile.type);

        const document = await strategyUploadApi.createDocument({
          fileType,
          mime: 'image/jpeg',
        });

        await uploadToS3WithRetry(document.uploadUrl, finalBlob);
        await strategyUploadApi.markAsUploaded({
          documentId: parseInt(document.id, 10),
        });

        onChange({
          documentId: parseInt(document.id, 10),
          viewUrl: document.viewUrl,
          fileType: document.fileType,
        });

        setImageSrc(null);
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';

        toast.success('Image uploaded successfully');
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(error instanceof Error ? error.message : 'Failed to upload image');
      } finally {
        setIsUploading(false);
      }
    },
    [imageSrc, selectedFile, onChange]
  );

  // --- Predefined Image Selection ---
  const handlePredefinedSelect = useCallback(
    (image: PredefinedImage) => {
      onChange({
        documentId: parseInt(image.documentId, 10),
        viewUrl: image.imageUrl || '',
        fileType: 'png',
      });
      setShowPredefined(false);
    },
    [onChange]
  );

  return (
    <div className="space-y-2">
      <Label>
        Strategy Image
        {required && <span className="text-red-500 ml-1">*</span>}
      </Label>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />

      {!value ? (
        <div className="space-y-3">
          {/* Predefined Image Dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowPredefined(!showPredefined)}
              className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg border border-admin-slate-300/20 bg-admin-dark-700 text-admin-slate-400 hover:border-admin-slate-300/40 transition-colors"
            >
              <span>Select predefined image...</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showPredefined ? 'rotate-180' : ''}`} />
            </button>

            {showPredefined && predefinedImages && (
              <div className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-admin-slate-300/20 bg-admin-dark-800 shadow-lg">
                {predefinedImages.map((img) => (
                  <button
                    key={img.id}
                    type="button"
                    onClick={() => handlePredefinedSelect(img)}
                    className="w-full flex items-center gap-3 px-4 py-2 hover:bg-admin-dark-700 transition-colors text-left"
                  >
                    {img.imageUrl && (
                      <img
                        src={img.imageUrl}
                        alt={img.label}
                        className="w-10 h-7 object-cover rounded"
                      />
                    )}
                    <span className="text-admin-slate-400">{img.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 border-t border-admin-slate-300/10" />
            <span className="text-sm text-admin-slate-500">or</span>
            <div className="flex-1 border-t border-admin-slate-300/10" />
          </div>

          {/* Custom Upload Button */}
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="w-full h-32 border-2 border-dashed"
          >
            {isUploading ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="w-8 h-8 animate-spin" />
                <span>Uploading...</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload className="w-8 h-8" />
                <span>Upload custom image</span>
                <span className="text-xs text-admin-slate-500">
                  {STRATEGY_IMAGE_DIMENSIONS.width}×{STRATEGY_IMAGE_DIMENSIONS.height}px
                </span>
              </div>
            )}
          </Button>
        </div>
      ) : (
        <div className="relative">
          <img
            src={value.viewUrl}
            alt="Strategy"
            className="w-full rounded-md border border-admin-slate-300/20"
            style={{ aspectRatio: `${STRATEGY_IMAGE_DIMENSIONS.aspectRatio}` }}
          />
          <Button
            type="button"
            variant="destructive"
            size="sm"
            className="absolute top-2 right-2"
            onClick={() => onChange(null)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Cropper Modal */}
      {imageSrc && (
        <StrategyImageCropper
          imageSrc={imageSrc}
          onCropComplete={handleCropComplete}
          onCancel={() => {
            setImageSrc(null);
            setSelectedFile(null);
          }}
        />
      )}
    </div>
  );
}
